import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
  EntityState,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import {
  fetchPathEnrollments,
  fetchEmployeeEnrollments,
} from "@/api/fetchEnrollments/fetchEnrollmentsApi";
import { unenrollLearningPath } from "@/api/unenrollLearningPath/unenrollLearningPathApi";
import {
  updateEnrollment,
  batchUnenroll,
} from "@/api/updateEnrollment/updateEnrollmentApi";
import type {
  PathEnrollment,
  UnenrollRequest,
  FetchEnrollmentsParams,
  UpdateEnrollmentRequest,
} from "@/modules/learningCenter/features/LearningPaths/apiTypes";
import type { BatchUnenrollResponse } from "@/api/updateEnrollment/types";

/**
 * Path Enrollments Slice
 * Manages enrollment assignments, unenrollments, and enrollment tracking
 * Part of Feature 012-after-a-learning: Learning Path Assignment to Employees
 */

// Entity adapter for normalized state management
const pathEnrollmentsAdapter = createEntityAdapter<PathEnrollment>({
  sortComparer: (a, b) => {
    // Sort by enrolledAt descending (most recently enrolled first)
    return new Date(b.enrolledAt).getTime() - new Date(a.enrolledAt).getTime();
  },
});

// State interface
interface PathEnrollmentsState {
  enrollments: EntityState<PathEnrollment, number>;
  selectedEnrollmentId: number | null;
  loading: boolean;
  error: string | null;
  unenrollLoading: boolean;
  unenrollError: string | null;
  updateLoading: boolean;
  updateError: string | null;
  filters: {
    status?: "active" | "inactive";
    companyId?: number;
    search?: string;
    includeInactive: boolean;
    dueSoon?: boolean;
    overdue?: boolean;
  };
  total: number;
}

// Initial state
const initialState: PathEnrollmentsState = {
  enrollments: pathEnrollmentsAdapter.getInitialState(),
  selectedEnrollmentId: null,
  loading: false,
  error: null,
  unenrollLoading: false,
  unenrollError: null,
  updateLoading: false,
  updateError: null,
  filters: {
    status: undefined,
    companyId: undefined,
    search: "",
    includeInactive: false,
    dueSoon: undefined,
    overdue: undefined,
  },
  total: 0,
};

// Async Thunks

/**
 * Fetch enrollments for a specific learning path
 */
export const fetchEnrollmentsForPath = createAsyncThunk(
  "pathEnrollments/fetchEnrollmentsForPath",
  async (
    { pathId, params }: { pathId: number; params?: FetchEnrollmentsParams },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchPathEnrollments(pathId, params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch path enrollments",
      );
    }
  },
);

/**
 * Fetch enrollments for a specific employee
 */
export const fetchEnrollmentsForEmployee = createAsyncThunk(
  "pathEnrollments/fetchEnrollmentsForEmployee",
  async (
    {
      employeeId,
      params,
    }: { employeeId: number; params?: Partial<FetchEnrollmentsParams> },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchEmployeeEnrollments(employeeId, params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch employee enrollments",
      );
    }
  },
);

// NOTE: the legacy `assignPath` thunk was removed in M2 with its dead
// assignLearningPath contract (the request/response shapes never matched the
// backend). Assignment flows through EnrollmentPage's
// POST /api/learning-paths/{id}/assign call (plan 2.9).

/**
 * Unenroll an employee from a learning path
 */
export const unenrollEnrollment = createAsyncThunk(
  "pathEnrollments/unenrollEnrollment",
  async (
    {
      enrollmentId,
      request,
    }: { enrollmentId: number; request?: UnenrollRequest },
    { rejectWithValue },
  ) => {
    try {
      await unenrollLearningPath(enrollmentId, request);
      return { enrollmentId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to unenroll employee",
      );
    }
  },
);

/**
 * Batch unenroll multiple employees
 */
export const batchUnenrollEnrollments = createAsyncThunk(
  "pathEnrollments/batchUnenroll",
  async (
    { enrollmentIds, reason }: { enrollmentIds: number[]; reason?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await batchUnenroll(enrollmentIds, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to batch unenroll employees",
      );
    }
  },
);

/**
 * Update enrollment due date
 */
export const updateEnrollmentDueDate = createAsyncThunk(
  "pathEnrollments/updateEnrollmentDueDate",
  async (
    { enrollmentId, dueDate }: { enrollmentId: number; dueDate: string | null },
    { rejectWithValue },
  ) => {
    try {
      const request: UpdateEnrollmentRequest = {
        enrollmentId,
        dueDate,
      };
      const response = await updateEnrollment(enrollmentId, request);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update enrollment due date",
      );
    }
  },
);

// Slice
const pathEnrollmentsSlice = createSlice({
  name: "pathEnrollments",
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<PathEnrollmentsState["filters"]>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearErrors: (state) => {
      state.error = null;
      state.unenrollError = null;
      state.updateError = null;
    },
    selectEnrollment: (state, action: PayloadAction<number | null>) => {
      state.selectedEnrollmentId = action.payload;
    },
    clearEnrollments: (state) => {
      state.enrollments = pathEnrollmentsAdapter.getInitialState();
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch enrollments for path
    builder
      .addCase(fetchEnrollmentsForPath.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnrollmentsForPath.fulfilled, (state, action) => {
        state.loading = false;
        const enrollments = action.payload?.data || [];
        pathEnrollmentsAdapter.setAll(state.enrollments, enrollments);
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchEnrollmentsForPath.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch enrollments for employee
    builder
      .addCase(fetchEnrollmentsForEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnrollmentsForEmployee.fulfilled, (state, action) => {
        state.loading = false;
        const enrollments = action.payload?.data || [];
        pathEnrollmentsAdapter.setAll(state.enrollments, enrollments);
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchEnrollmentsForEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Unenroll enrollment
    builder
      .addCase(unenrollEnrollment.pending, (state) => {
        state.unenrollLoading = true;
        state.unenrollError = null;
      })
      .addCase(unenrollEnrollment.fulfilled, (state, action) => {
        state.unenrollLoading = false;
        // Remove from list if filters exclude inactive
        if (!state.filters.includeInactive) {
          pathEnrollmentsAdapter.removeOne(
            state.enrollments,
            action.payload.enrollmentId,
          );
          state.total -= 1;
        }
        // Clear selection if unenrolled
        if (state.selectedEnrollmentId === action.payload.enrollmentId) {
          state.selectedEnrollmentId = null;
        }
      })
      .addCase(unenrollEnrollment.rejected, (state, action) => {
        state.unenrollLoading = false;
        state.unenrollError = action.payload as string;
      });

    // Batch unenroll
    builder
      .addCase(batchUnenrollEnrollments.pending, (state) => {
        state.unenrollLoading = true;
        state.unenrollError = null;
      })
      .addCase(batchUnenrollEnrollments.fulfilled, (state, action) => {
        state.unenrollLoading = false;
        // Remove successfully unenrolled enrollments if filters exclude inactive
        if (!state.filters.includeInactive && action.payload.unenrolled) {
          action.payload.unenrolled.forEach((item) => {
            pathEnrollmentsAdapter.removeOne(
              state.enrollments,
              item.enrollmentId,
            );
          });
          state.total -= action.payload.success;
        }
      })
      .addCase(batchUnenrollEnrollments.rejected, (state, action) => {
        state.unenrollLoading = false;
        state.unenrollError = action.payload as string;
      });

    // Update enrollment due date
    builder
      .addCase(updateEnrollmentDueDate.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateEnrollmentDueDate.fulfilled, (state, action) => {
        state.updateLoading = false;
        // Update the enrollment in the normalized state
        pathEnrollmentsAdapter.upsertOne(state.enrollments, action.payload);
      })
      .addCase(updateEnrollmentDueDate.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });
  },
});

// Actions
export const { setFilters, clearErrors, selectEnrollment, clearEnrollments } =
  pathEnrollmentsSlice.actions;

// Selectors using entity adapter
export const {
  selectAll: selectAllEnrollmentsEntities,
  selectById: selectEnrollmentById,
  selectIds: selectEnrollmentIds,
  selectEntities: selectEnrollmentEntities,
  selectTotal: selectEnrollmentsCount,
} = pathEnrollmentsAdapter.getSelectors(
  (state: RootState) => state.pathEnrollments.enrollments,
);

// Custom selectors
export const selectAllEnrollments = (state: RootState) => {
  const enrollments = selectAllEnrollmentsEntities(state);
  const filters = state.pathEnrollments.filters;

  // Apply filters
  return enrollments.filter((enrollment) => {
    // Filter by search query (employee name or email)
    if (filters.search) {
      const query = filters.search.toLowerCase();
      const matchesName = enrollment.employeeName.toLowerCase().includes(query);
      const matchesEmail = enrollment.employeeEmail
        .toLowerCase()
        .includes(query);
      if (!matchesName && !matchesEmail) {
        return false;
      }
    }

    // Filter by status
    if (filters.status !== undefined && enrollment.status !== filters.status) {
      return false;
    }

    // Filter by company
    if (
      filters.companyId !== undefined &&
      enrollment.employeeCompanyId !== filters.companyId
    ) {
      return false;
    }

    // Filter by includeInactive
    if (!filters.includeInactive && enrollment.status === "inactive") {
      return false;
    }

    // Filter by due soon (within 7 days)
    if (filters.dueSoon && enrollment.dueDate) {
      const dueDate = new Date(enrollment.dueDate);
      const now = new Date();
      const daysRemaining = Math.ceil(
        (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (daysRemaining > 7 || daysRemaining < 0) {
        return false;
      }
    }

    // Filter by overdue
    if (filters.overdue && enrollment.dueDate) {
      const dueDate = new Date(enrollment.dueDate);
      const now = new Date();
      if (dueDate >= now) {
        return false;
      }
    }

    return true;
  });
};

export const selectActiveEnrollments = (state: RootState) => {
  return selectAllEnrollmentsEntities(state).filter(
    (enrollment) => enrollment.status === "active",
  );
};

export const selectEnrollmentsByPath =
  (pathId: number) => (state: RootState) => {
    return selectAllEnrollmentsEntities(state).filter(
      (enrollment) =>
        enrollment.pathId === pathId && enrollment.status === "active",
    );
  };

export const selectEnrollmentsByEmployee =
  (employeeId: number) => (state: RootState) => {
    return selectAllEnrollmentsEntities(state).filter(
      (enrollment) =>
        enrollment.employeeId === employeeId && enrollment.status === "active",
    );
  };

export const selectSelectedEnrollmentId = (state: RootState) =>
  state.pathEnrollments.selectedEnrollmentId;

export const selectSelectedEnrollment = (state: RootState) => {
  const selectedId = state.pathEnrollments.selectedEnrollmentId;
  if (!selectedId) return null;
  return selectEnrollmentById(state, selectedId);
};

export const selectEnrollmentsLoading = (state: RootState) =>
  state.pathEnrollments.loading;

export const selectEnrollmentsError = (state: RootState) =>
  state.pathEnrollments.error;

export const selectUnenrollLoading = (state: RootState) =>
  state.pathEnrollments.unenrollLoading;

export const selectUnenrollError = (state: RootState) =>
  state.pathEnrollments.unenrollError;

export const selectUpdateLoading = (state: RootState) =>
  state.pathEnrollments.updateLoading;

export const selectUpdateError = (state: RootState) =>
  state.pathEnrollments.updateError;

export const selectEnrollmentsFilters = (state: RootState) =>
  state.pathEnrollments.filters;

export const selectEnrollmentsTotal = (state: RootState) =>
  state.pathEnrollments.total;

// Reducer
export default pathEnrollmentsSlice.reducer;
